import { faCalendar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { DatesRangeValue, DateStringValue, DayProps } from '@mantine/dates'
import { DatePickerInput } from '@mantine/dates'
import { IRRIGATION_SCHEDULER_FIRST_DAY_OF_WEEK } from '@semios/app-platform-values-helpers-and-types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useState } from 'react'
import { colors } from 'settings/colors'
import {
  snapBackwardToWeekEnd,
  snapForwardToWeekStart,
  snapToWeekEnd,
  snapToWeekStart,
} from './weekRangeUtils'

export const WeekRangePicker = ({
  disabled,
  label,
  maxDate,
  minDate,
  onChange,
  placeholder,
  value,
}: {
  disabled?: boolean
  label?: string
  maxDate?: Date
  minDate?: Date
  onChange: (value: [Date | null, Date | null]) => void
  placeholder?: string
  value: [Date | null, Date | null]
}) => {
  const [hoveredDate, setHoveredDate] = useState<DateStringValue | null>(null)
  const browserTimezone = moment.tz.guess()

  const getDayProps = (date: DateStringValue): Partial<DayProps> => {
    const dayProps: Partial<DayProps> = { onMouseEnter: () => setHoveredDate(date) }

    if (!hoveredDate) return dayProps

    const [selectedStart, selectedEnd] = value
    const day = moment.tz(date, browserTimezone)

    const previewStart =
      selectedStart && !selectedEnd
        ? snapToWeekStart(moment.tz(selectedStart, browserTimezone))
        : snapToWeekStart(moment.tz(hoveredDate, browserTimezone))

    const previewEnd = snapToWeekEnd(moment.tz(hoveredDate, browserTimezone))

    if (previewEnd.isBefore(previewStart) || !day.isBetween(previewStart, previewEnd, 'day', '[]')) {
      return dayProps
    }

    return {
      ...dayProps,
      inRange: true,
      firstInRange: day.isSame(previewStart, 'day'),
      lastInRange: day.isSame(previewEnd, 'day'),
    }
  }

  const handleChange = (e: DatesRangeValue<DateStringValue>) => {
    const [startDate, endDate] = e

    onChange([
      startDate ? snapToWeekStart(moment.tz(startDate, browserTimezone)).toDate() : null,
      endDate ? snapToWeekEnd(moment.tz(endDate, browserTimezone)).toDate() : null,
    ])
  }

  return (
    <DatePickerInput
      type="range"
      value={value}
      label={label}
      placeholder={placeholder}
      rightSection={<FontAwesomeIcon css={{ color: colors.grey800, fontSize: 16 }} icon={faCalendar} />}
      disabled={disabled}
      onChange={handleChange}
      miw={240}
      valueFormat={translate.dates.getMomentFormat('MMM D, YYYY')}
      maxLevel={'year'}
      firstDayOfWeek={IRRIGATION_SCHEDULER_FIRST_DAY_OF_WEEK}
      getDayProps={getDayProps}
      minDate={minDate ? snapForwardToWeekStart(moment.tz(minDate, browserTimezone)).toDate() : undefined}
      maxDate={maxDate ? snapBackwardToWeekEnd(moment.tz(maxDate, browserTimezone)).toDate() : undefined}
    />
  )
}
